import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listArrivalDisputes, resolveArrivalDispute } from "@/lib/arrival-dispute.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { ShiftTimelineButton } from "@/components/ShiftTimelineButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/admin/arrival-disputes")({
  head: () => ({ meta: [{ title: "Arrival Disputes — Admin" }] }),
  component: AdminArrivalDisputes,
});

type Item = Awaited<ReturnType<typeof listArrivalDisputes>>["items"][number];

function AdminArrivalDisputes() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listArrivalDisputes);
  const resolve = useServerFn(resolveArrivalDispute);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<Item | null>(null);
  const [decision, setDecision] = useState<"upheld" | "dismissed">("upheld");
  const [adjusted, setAdjusted] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await load(); setItems(r.items); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
  }, []);

  const startResolve = (it: Item) => {
    setResolving(it);
    setDecision("upheld");
    setAdjusted("");
    setNote("");
  };

  const submit = async () => {
    if (!resolving) return;
    setBusy(true);
    try {
      const r = await resolve({ data: {
        booking_id: resolving.booking_id,
        decision,
        adjusted_arrival_at: decision === "upheld" && adjusted ? new Date(adjusted).toISOString() : undefined,
        note: note.trim() || undefined,
      }});
      if ("error" in r) throw new Error(r.error);
      toast.success(decision === "upheld"
        ? `Upheld — deduction is now £${(r.new_deduction_pence / 100).toFixed(2)} (${r.late_minutes} min late).`
        : "Dispute dismissed. Original deduction stands.");
      setResolving(null);
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin</p>
            <h1 className="mt-3 font-display text-3xl">Arrival Disputes</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Console</Link>
            <Link to="/admin/disputes" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">All Disputes</Link>
            <Link to="/admin/timeline" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Timeline</Link>
          </div>
        </div>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Bartenders may dispute a logged arrival time within 48 hours. Upholding adjusts the deduction (and queues an adjustment payout if the shift is already settled).
        </p>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 border border-border bg-card p-8 text-center">
            <p className="font-display text-lg">No arrival disputes.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {items.map((d) => (
              <article key={d.booking_id} className="border border-border bg-card p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <p className={`text-xs tracking-wide-luxury ${d.status === "open" ? "text-destructive" : "text-muted-foreground"}`}>
                      {d.status === "open" ? "Open" : d.status.toUpperCase()} · Raised {new Date(d.arrival_disputed_at).toLocaleString()}
                    </p>
                    <p className="mt-1 font-display text-lg">{d.bartender_name} vs {d.venue_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(d.shift_start).toLocaleString()} — {new Date(d.shift_end).toLocaleTimeString()} · {d.role_type} · £{d.hourly_rate}/hr · {d.address ?? "—"} {d.postcode ?? ""}
                    </p>
                    <div className="mt-3 border border-border bg-background p-3 text-xs">
                      <p><strong>Venue logged arrival:</strong> {d.actual_arrival_at ? new Date(d.actual_arrival_at).toLocaleString() : "—"}</p>
                      <p><strong>Current deduction:</strong> £{(d.late_deduction_pence / 100).toFixed(2)}</p>
                      <p className="mt-2 whitespace-pre-wrap"><strong>Bartender's reason:</strong> {d.reason || "(no reason captured)"}</p>
                      {d.resolution && <p className="mt-2"><strong>Resolution:</strong> {d.resolution}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <ShiftTimelineButton shiftId={d.shift_id} label="Timeline" />
                    {d.status === "open" && (
                      <Button onClick={() => startResolve(d)}
                        className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
                        Review & resolve
                      </Button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!resolving} onOpenChange={(o) => { if (!o) setResolving(null); }}>
        <DialogContent className="rounded-none border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Resolve arrival dispute</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button variant={decision === "upheld" ? "default" : "outline"} onClick={() => setDecision("upheld")}
                className="rounded-none flex-1 tracking-wide-luxury">Uphold (adjust)</Button>
              <Button variant={decision === "dismissed" ? "default" : "outline"} onClick={() => setDecision("dismissed")}
                className="rounded-none flex-1 tracking-wide-luxury">Dismiss</Button>
            </div>
            {decision === "upheld" && (
              <div>
                <Label className="text-xs tracking-wide-luxury">Adjusted arrival time (leave blank = arrived on time, full deduction removed)</Label>
                <Input type="datetime-local" value={adjusted} onChange={(e) => setAdjusted(e.target.value)} className="rounded-none" />
              </div>
            )}
            <div>
              <Label className="text-xs tracking-wide-luxury">Internal note (recorded in audit trail)</Label>
              <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value.slice(0, 2000))}
                placeholder="e.g. Reviewed CCTV — bartender arrived at 19:00 as claimed." className="rounded-none" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResolving(null)} className="rounded-none tracking-wide-luxury">Cancel</Button>
            <Button onClick={submit} disabled={busy}
              className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
              {busy ? "Saving…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
